import clsx from "clsx";
import format from "date-fns/format";
import parseISO from "date-fns/parseISO";
import React, { useEffect, useState } from "react";

import Box from "@material-ui/core/Box";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableRow from "@material-ui/core/TableRow";
import useTheme from "@material-ui/core/styles/useTheme";
import useMediaQuery from "@material-ui/core/useMediaQuery";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import Accordion from "components/Accordion";
import Loader from "components/Loader";
import Pagination from "components/Pagination";
import PaperCarousel from "components/PaperCarousel";
import { useRacehorse360Api } from "hooks/api";
import { SortOrder } from "interfaces/SortOrder";
import {
  parseCourseSurfaceForPC,
  parseInjuryTrackCondition,
  parseInjuryType
} from "utils/enum-parser";
import ViewComments from "./ViewComments";

import useStyles, { HEADER_HEIGHT, ROW_HEIGHT } from "./styles";

export interface Props {
  horse: racehorse360.IHorse;
  onLoad?: (items, pagingInfo) => void;
  onHeightChange?: (minHeight: number) => void;
}

const EIDData = (props: Props) => {
  const { horse, onLoad, onHeightChange } = props;
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });
  const { useListHorseInjuries, useListHorseInjuryDetails } =
    useRacehorse360Api();
  const [activePage, setActivePage] = useState<number>(0);
  const PAGE_SIZE = 1;

  const { isLoading: horseInjuriesLoading, data: listHorseInjuriesData } =
    useListHorseInjuries(
      {
        query: {
          horseIds: horse?.id ? [horse.id] : null
        },
        pagingOptions: {
          maxResults: 99,
          offset: 0
        },
        getOptions: {
          select: [
            "id",
            "incompass_injury_id",
            "incident_date",
            "facility.code",
            "horse",
            "injury_type",
            "course_surface",
            "track_condition",
            "location",
            "trainer",
            "jockey",
            "official_vet",
            "attending_vet",
            "medications",
            "comments"
          ],
          orderBy: [`incidentDate ${SortOrder.DESC}`]
        }
      },
      {
        onSuccess: data => {
          onLoad && onLoad(data.horseInjuries, data.pagingInfo);
        }
      }
    );

  const horseInjuriesData = listHorseInjuriesData?.horseInjuries || [];

  const {
    isLoading: horseInjuriesDetailLoading,
    data: listHorseInjuryDetails
  } = useListHorseInjuryDetails(
    {
      query: {
        horseInjuryIds: horseInjuriesData?.map(item => item.id)
      },
      pagingOptions: {
        maxResults: 99,
        offset: 0
      },
      getOptions: {
        select: ["id", "HorseInjury.id", "sequence", "detail"]
      }
    },
    {
      enabled: !!horseInjuriesData.length
    }
  );

  const horseInjuriesDetailsData =
    listHorseInjuryDetails?.horseInjuryDetails || [];

  const horseInjuriesMappedWithDetails = horseInjuriesData?.map(horseInjury => {
    const details = horseInjuriesDetailsData?.filter(
      detail => detail.horseInjury.id === horseInjury.id
    );
    return {
      ...horseInjury,
      details: details
    };
  });

  const handleSlideChange = index => {
    setActivePage(index);
  };

  const handlePageChange = page => {
    setActivePage(page - 1);
  };

  useEffect(() => {
    if (listHorseInjuriesData?.horseInjuries && onLoad) {
      onLoad(
        listHorseInjuriesData?.horseInjuries,
        listHorseInjuriesData?.pagingInfo
      );
    }
  }, []);

  useEffect(() => {
    if (!horseInjuriesLoading && !horseInjuriesDetailLoading) {
      //max height prediction
      const maxDetailsCount = Math.max(
        ...horseInjuriesMappedWithDetails.map(hi => hi.details.length)
      );
      onHeightChange &&
        onHeightChange(
          HEADER_HEIGHT + ROW_HEIGHT * 5 + maxDetailsCount * ROW_HEIGHT
        );
    }
  }, [horseInjuriesLoading, horseInjuriesDetailLoading]);

  const renderTable = horseInjury => {
    return (
      <>
        {horseInjuriesLoading || horseInjuriesDetailLoading ? (
          <Loader />
        ) : (
          <>
            <Box className={classes.eidHeader}>
              <Box
                className={clsx(classes.eidTitle, {
                  [classes.eidTitleLight]: isMobile
                })}
              >
                {horseInjury?.incidentDate &&
                  format(
                    parseISO(horseInjury.incidentDate),
                    isMobile ? "M/d/yy" : "MMM d, yyyy"
                  )}
                {horseInjury?.facility.code &&
                  ` -  ${horseInjury.facility.code}`}
              </Box>
              <ViewComments horseInjury={horseInjury} />
            </Box>
            <TableContainer className={classes.tableContainer}>
              <Table className={classes.table}>
                <TableBody>
                  <TableRow>
                    <TableCell className={classes.eidTableCell}>
                      Type / Surf / Cond
                    </TableCell>
                    <TableCell className={classes.eidTableCell} align={"right"}>
                      {`${parseInjuryType(horseInjury?.injuryType) || "- "} /
                      ${
                        parseCourseSurfaceForPC(horseInjury?.courseSurface) ||
                        " - "
                      }
                        / ${
                          parseInjuryTrackCondition(
                            horseInjury?.trackCondition
                          ) || "-"
                        }`}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.eidTableCell}>
                      Location
                    </TableCell>
                    <TableCell className={classes.eidTableCell} align={"right"}>
                      {horseInjury?.location || "-"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.eidTableCell}>
                      Trainer / Jockey-Rider
                    </TableCell>
                    <TableCell className={classes.eidTableCell} align={"right"}>
                      {horseInjury?.trainer
                        ? `${horseInjury.trainer?.firstName} ${horseInjury.trainer?.lastName} `
                        : "- "}
                      /
                      {horseInjury?.jockey
                        ? ` ${horseInjury.jockey?.firstName} ${horseInjury.jockey?.lastName}`
                        : " -"}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.eidTableCell}>
                      Official / Private Vet
                    </TableCell>
                    <TableCell className={classes.eidTableCell} align={"right"}>
                      {`${horseInjury?.officialVet || "-"} / ${
                        horseInjury?.attendingVet || "-"
                      }`}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell className={classes.eidTableCell}>
                      Medications
                    </TableCell>
                    <TableCell className={classes.eidTableCell} align={"right"}>
                      {horseInjury?.medications || "-"}
                    </TableCell>
                  </TableRow>

                  {horseInjury?.details.length ? (
                    horseInjury?.details.map((injuryDetail, index) => {
                      return (
                        <TableRow
                          key={injuryDetail?.id}
                          className={classes.eidTableRowSpecific}
                        >
                          <TableCell className={classes.eidTableCell}>
                            {index === 0 ? "Details" : null}
                          </TableCell>
                          <TableCell
                            className={classes.eidTableCell}
                            align={"right"}
                          >
                            {injuryDetail.detail || "-"}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow className={classes.eidTableRowSpecific}>
                      <TableCell className={classes.eidTableCell}>
                        Details
                      </TableCell>
                      <TableCell
                        className={classes.eidTableCell}
                        align={"right"}
                      >
                        -
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </>
    );
  };

  return (
    <>
      {isMobile ? (
        <Accordion
          className={classes.accordion}
          count={horseInjuriesMappedWithDetails?.length}
          hideBackground
          hideShadow
          roundCount
          name={"EID Data"}
          hideSubtitleWhenCollapsed
          disabled={horseInjuriesMappedWithDetails?.length === 0}
          subtitle={
            horseInjuriesMappedWithDetails?.length
              ? `Showing ${activePage + 1} of ${
                  horseInjuriesMappedWithDetails?.length
                }`
              : null
          }
        >
          <PaperCarousel
            items={horseInjuriesMappedWithDetails}
            itemRenderer={renderTable}
            onSlideChange={handleSlideChange}
          />
        </Accordion>
      ) : (
        <>
          {horseInjuriesData?.length ? (
            <>
              {renderTable(horseInjuriesMappedWithDetails[activePage])}
              <Pagination
                totalCount={horseInjuriesMappedWithDetails?.length}
                pageNumber={activePage + 1}
                pageSize={PAGE_SIZE}
                onPageChange={handlePageChange}
              />
            </>
          ) : (
            <div className={classes.emptyDataHolder}>No EID Data</div>
          )}
        </>
      )}
    </>
  );
};

export default EIDData;
